import { inject, Injectable } from '@angular/core';
import { UserService } from '../../shared/services/user.service';
import { take } from 'rxjs';

type TagContext = {
  region?: string;
  cedis?: string;
  year?: number;
  usuario?: string;      
  module_name?: string; 
  profile?: string;
  channel?: string;
  source?: string;
};

const CTX_KEY = 'gtm_user_context_v1';

@Injectable({
  providedIn: 'root'
})
export class TagManagerService {
  
  private _userService = inject(UserService);

  private ctx: TagContext = {
    year: new Date().getFullYear(),
    ...this.readCtxFromStorage(),
  };

  private get dataLayer(): any[] {
    (window as any).dataLayer = (window as any).dataLayer || [];
    return (window as any).dataLayer;
  }


  private readCtxFromStorage(): Partial<TagContext> {
    try {
      const raw = localStorage.getItem(CTX_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ? parsed : {};
    } catch {
      return {};
    }
  }

  private saveCtxToStorage() {
    try {
      localStorage.setItem(CTX_KEY, JSON.stringify(this.ctx));
    } catch {
      // ignore
    }
  }

  setContext(partial: Partial<TagContext>) {
    this.ctx = {
      ...this.ctx,
      ...partial,
      year: partial.year ?? this.ctx.year ?? new Date().getFullYear(),
    };
    this.saveCtxToStorage();
  }

  setModule(moduleName?: string) {
    this.setContext({ module_name: moduleName });
  }

  clearContext() {
    this.ctx = { year: new Date().getFullYear() };
    try { localStorage.removeItem(CTX_KEY); } catch {}
    this.dataLayer.push({ event: 'user_context_cleared' });
  }


  hydrateUserContextFromApi() {
    this._userService.getUserDataDetail().pipe(take(1)).subscribe({
      next: (value) => {
        this.setContext({
          region: value.region,
          cedis: value.cedis ?? 'No.' ?? value.cedisId,
          usuario: value.gpid,
          profile: value.profile,
          channel: value.channel,
          source: 'Web',
          year: new Date().getFullYear(),
        });

        this.dataLayer.push({ event: 'user_context', ...this.ctx });
      },
    });
  }

  push(event: Record<string, any>) {
    this.dataLayer.push({
      ...this.ctx,
      ...event,
    });
  }

  pageView(pagePath: string, pageTitle?: string) {
    const title = pageTitle ?? document.title;

    this.push({
      event: 'page_view_spa',
      page_path: pagePath,
      page_title: title,
      page_location: window.location.href,
      module_name: this.ctx.module_name ?? title,
    });
  }

  moduleEvent(moduleName: string, action: string, extra?: Record<string, any>) {
    this.setModule(moduleName);

    this.push({
      event: 'module_interaction',
      module_name: this.ctx.module_name,
      action,
      ...extra,
    });
  }

  openPostDetail(postId:number, sessionId: string) {
    this.push({
      event: 'open_post',
      post_id: postId,
      post_session_id: sessionId
    });
  }

  closePostDetail(postId:number, sessionId: string, duration: number) {
    this.push({
      event: 'close_post', 
      post_id: postId,
      post_session_id: sessionId,
      duration: duration
    });
  }

  redeemBtn(transactionId: string) {
    this.push({
      event: 'redeem',
      usuario: this.ctx.usuario,
      transaction_id: transactionId
    });
  }
}
  
